import * as THREE from 'three';
import { Entity } from '../../entities';
import {
  createGlowMaterial,
  createHaloMaterial,
  createMatteMaterial,
  palette
} from '../../visual';

export interface StationOptions {
  readonly position: THREE.Vector3;
  readonly rotationY?: number;
  readonly scale?: number;
}

const DEFAULT_STATION_SCALE = 4.2;
const SPHERE_RADIUS = 8.0;
const HALO_RADIUS = 14.5;
const SPIRE_HEIGHT = 10.0;

/**
 * Station — a Space Anomaly inspired hub. A monolithic pearl sphere with a
 * horizontal luminous "eye" slit wrapping the equator, a tall crown spire,
 * an engineering nexus at the bottom, and radial docking arms. Minimal
 * secondary structure so the silhouette reads strong from a distance.
 */
export class Station extends Entity {
  readonly collisionRadius: number;
  readonly collisionStatic = true;
  readonly collisionRestitution = 0.3;
  readonly interactionRadius: number;
  readonly approachRadius: number;

  private readonly halo: THREE.Group;
  private readonly aura: THREE.Mesh;
  private readonly beacons: THREE.Mesh[] = [];
  private readonly strobes: THREE.Mesh[] = [];
  private readonly slitInner: THREE.MeshBasicMaterial;
  private readonly slitOuter: THREE.MeshBasicMaterial;
  private readonly entryBeam: THREE.Mesh;
  private readonly scaleFactor: number;
  private time = 0;

  constructor(options: StationOptions) {
    super();
    this.object3D.name = 'Station';
    this.object3D.position.copy(options.position);
    this.object3D.rotation.y = options.rotationY ?? 0;
    this.scaleFactor = options.scale ?? DEFAULT_STATION_SCALE;
    this.collisionRadius = (SPHERE_RADIUS + 0.5) * this.scaleFactor;
    this.interactionRadius = (HALO_RADIUS + 6) * this.scaleFactor;
    this.approachRadius = (HALO_RADIUS + 48) * this.scaleFactor;

    const { inner, outer, beam } = this.buildSphere();
    this.slitInner = inner;
    this.slitOuter = outer;
    this.entryBeam = beam;

    this.buildNexus();
    this.buildSpire();
    this.buildDockingArms();
    this.halo = this.buildHalo();
    this.aura = this.buildAura();

    this.object3D.add(this.halo, this.aura);
    this.object3D.scale.setScalar(this.scaleFactor);
  }

  override update(dt: number): void {
    this.time += dt;

    this.halo.rotation.y += 0.08 * dt;

    for (const beacon of this.beacons) {
      beacon.scale.setScalar(0.85 + Math.sin(this.time * 2.2) * 0.15);
    }

    const strobeOn = Math.floor(this.time * 1.6) % 2 === 0;
    for (const strobe of this.strobes) strobe.visible = strobeOn;

    const slitPulse = 0.72 + Math.sin(this.time * 0.9) * 0.18;
    this.slitInner.opacity = 0.85 * slitPulse;
    this.slitOuter.opacity = 0.55 * slitPulse;

    const beamPulse = 0.7 + Math.sin(this.time * 1.6) * 0.3;
    (this.entryBeam.material as THREE.MeshBasicMaterial).opacity =
      0.6 * beamPulse;

    (this.aura.material as THREE.MeshBasicMaterial).opacity =
      0.09 + Math.sin(this.time * 0.5) * 0.02;
  }

  getCollisionCenter(out: THREE.Vector3): THREE.Vector3 {
    return out.copy(this.object3D.position);
  }

  /** World position where ships should spawn when leaving the station. */
  getExitPosition(out: THREE.Vector3): THREE.Vector3 {
    out.set(0, 0, (HALO_RADIUS + 12) * this.scaleFactor);
    out.applyQuaternion(this.object3D.quaternion);
    out.add(this.object3D.position);
    return out;
  }

  private buildSphere(): {
    inner: THREE.MeshBasicMaterial;
    outer: THREE.MeshBasicMaterial;
    beam: THREE.Mesh;
  } {
    // Main pearlescent shell.
    const shellTop = createMatteMaterial({
      color: 0xe4e8f3,
      roughness: 0.35
    });
    const shellBottom = createMatteMaterial({
      color: 0xb1b7c7,
      roughness: 0.55
    });

    const upperHalf = new THREE.Mesh(
      new THREE.SphereGeometry(
        SPHERE_RADIUS,
        64,
        32,
        0,
        Math.PI * 2,
        0,
        Math.PI / 2
      ),
      shellTop
    );
    upperHalf.position.y = 0.45;
    this.object3D.add(upperHalf);

    const lowerHalf = new THREE.Mesh(
      new THREE.SphereGeometry(
        SPHERE_RADIUS,
        64,
        32,
        0,
        Math.PI * 2,
        Math.PI / 2,
        Math.PI / 2
      ),
      shellBottom
    );
    lowerHalf.position.y = -0.45;
    this.object3D.add(lowerHalf);

    // Fine shell accent ring.
    const trimMat = createMatteMaterial({
      color: palette.station.trim,
      roughness: 0.5
    });
    const equatorTrim = new THREE.Mesh(
      new THREE.TorusGeometry(SPHERE_RADIUS, 0.18, 14, 96),
      trimMat
    );
    equatorTrim.rotation.x = Math.PI / 2;
    this.object3D.add(equatorTrim);

    // --- SIGNATURE EQUATOR "EYE" SLIT -------------------------------
    // Two stacked emissive bands spanning the full equator, with a narrow
    // dark spacer. The inner band pulses like a pulse of life inside.
    const slitInnerMat = createGlowMaterial(0xb4e3ff, 0.9, true);
    const slitOuterMat = createGlowMaterial(0x5a9bd6, 0.55, true);

    // Inner bright core (thin strip).
    const slitInner = new THREE.Mesh(
      new THREE.CylinderGeometry(
        SPHERE_RADIUS + 0.02,
        SPHERE_RADIUS + 0.02,
        0.35,
        96,
        1,
        true
      ),
      slitInnerMat
    );
    slitInner.position.y = 0;
    this.object3D.add(slitInner);

    // Outer halo band (wider/softer).
    const slitOuter = new THREE.Mesh(
      new THREE.CylinderGeometry(
        SPHERE_RADIUS + 0.04,
        SPHERE_RADIUS + 0.04,
        0.9,
        96,
        1,
        true
      ),
      slitOuterMat
    );
    slitOuter.position.y = 0;
    this.object3D.add(slitOuter);

    // Slit trim lines (top & bottom) — hard edges that give definition.
    const slitEdge = createMatteMaterial({
      color: 0x20252d,
      roughness: 0.7
    });
    const topEdge = new THREE.Mesh(
      new THREE.TorusGeometry(SPHERE_RADIUS + 0.05, 0.05, 10, 96),
      slitEdge
    );
    topEdge.rotation.x = Math.PI / 2;
    topEdge.position.y = 0.5;
    this.object3D.add(topEdge);

    const botEdge = topEdge.clone();
    botEdge.position.y = -0.5;
    this.object3D.add(botEdge);

    // Window cells along the slit (makes the slit feel like inhabited floors).
    const cellMat = createGlowMaterial(0xffffff, 1, true);
    const cellGeo = new THREE.BoxGeometry(0.2, 0.26, 0.06);
    const cellCount = 64;
    for (let i = 0; i < cellCount; i++) {
      if (Math.random() < 0.25) continue;
      const angle = (i / cellCount) * Math.PI * 2;
      const px = Math.cos(angle) * (SPHERE_RADIUS + 0.06);
      const pz = Math.sin(angle) * (SPHERE_RADIUS + 0.06);
      const cell = new THREE.Mesh(cellGeo, cellMat);
      cell.position.set(px, 0, pz);
      cell.lookAt(px * 2, 0, pz * 2);
      this.object3D.add(cell);
    }

    // --- ENTRY BEACON -----------------------------------------------
    // A brighter "lighthouse" blob on the +Z side of the slit (the docking eye).
    const entryZ = SPHERE_RADIUS + 0.08;
    const entryGlowMat = createGlowMaterial(0xffffff, 0.95, true);
    const entryEye = new THREE.Mesh(
      new THREE.SphereGeometry(0.75, 18, 14),
      entryGlowMat
    );
    entryEye.scale.set(1.6, 1, 0.6);
    entryEye.position.set(0, 0, entryZ);
    this.object3D.add(entryEye);
    this.beacons.push(entryEye);

    // Approach cone projecting outward.
    const beam = new THREE.Mesh(
      new THREE.ConeGeometry(1.2, 6, 18, 1, true),
      createGlowMaterial(0xb4e3ff, 0.45, true)
    );
    beam.rotation.x = Math.PI / 2;
    beam.position.set(0, 0, entryZ + 3);
    this.object3D.add(beam);

    // Docking aperture frame.
    const frameMat = createMatteMaterial({
      color: 0x1a1e2a,
      roughness: 0.8
    });
    const frameRing = new THREE.Mesh(
      new THREE.TorusGeometry(1.4, 0.12, 10, 28),
      frameMat
    );
    frameRing.position.set(0, 0, entryZ - 0.1);
    frameRing.rotation.y = Math.PI / 2;
    this.object3D.add(frameRing);

    const frameAccent = new THREE.Mesh(
      new THREE.TorusGeometry(1.4, 0.05, 8, 28),
      createGlowMaterial(0xb4e3ff, 0.9, true)
    );
    frameAccent.position.set(0, 0, entryZ);
    frameAccent.rotation.y = Math.PI / 2;
    this.object3D.add(frameAccent);
    this.beacons.push(frameAccent);

    // Little marker lights on either side of the entry.
    const markerMat = createGlowMaterial(0xffe38a, 1, true);
    const markerGeo = new THREE.SphereGeometry(0.15, 10, 8);
    const markerL = new THREE.Mesh(markerGeo, markerMat);
    markerL.position.set(-1.9, 0, entryZ);
    const markerR = markerL.clone();
    markerR.position.x = 1.9;
    this.object3D.add(markerL, markerR);
    this.strobes.push(markerL, markerR);

    return { inner: slitInnerMat, outer: slitOuterMat, beam };
  }

  private buildNexus(): void {
    // Boxy engineering section underneath the sphere.
    const hullMat = createMatteMaterial({
      color: 0x2b3240,
      roughness: 0.75
    });
    const trimMat = createMatteMaterial({
      color: palette.station.hullDark,
      roughness: 0.8
    });
    const windowMat = createGlowMaterial(palette.station.window, 1);
    const accentMat = createGlowMaterial(0xb4e3ff, 0.85, true);

    const nexus = new THREE.Mesh(
      new THREE.CylinderGeometry(3.4, 3.8, 1.8, 8),
      hullMat
    );
    nexus.position.y = -SPHERE_RADIUS - 0.5;
    this.object3D.add(nexus);

    const nexusTrim = new THREE.Mesh(
      new THREE.TorusGeometry(3.6, 0.12, 10, 32),
      trimMat
    );
    nexusTrim.rotation.x = Math.PI / 2;
    nexusTrim.position.y = -SPHERE_RADIUS - 1.35;
    this.object3D.add(nexusTrim);

    const nexusAccentStrip = new THREE.Mesh(
      new THREE.TorusGeometry(3.5, 0.035, 8, 32),
      accentMat
    );
    nexusAccentStrip.rotation.x = Math.PI / 2;
    nexusAccentStrip.position.y = -SPHERE_RADIUS + 0.05;
    this.object3D.add(nexusAccentStrip);

    // Windows around the nexus.
    const winGeo = new THREE.BoxGeometry(0.5, 0.35, 0.1);
    for (let i = 0; i < 14; i++) {
      if (Math.random() < 0.2) continue;
      const angle = (i / 14) * Math.PI * 2 + Math.PI / 16;
      const r = 3.65;
      const px = Math.cos(angle) * r;
      const pz = Math.sin(angle) * r;
      const w = new THREE.Mesh(winGeo, windowMat);
      w.position.set(px, -SPHERE_RADIUS - 0.5, pz);
      w.lookAt(px * 2, -SPHERE_RADIUS - 0.5, pz * 2);
      this.object3D.add(w);
    }

    // Bottom cap with a beacon.
    const capMat = createMatteMaterial({ color: 0x101520, roughness: 0.85 });
    const cap = new THREE.Mesh(
      new THREE.CylinderGeometry(2.4, 1.8, 0.5, 8),
      capMat
    );
    cap.position.y = -SPHERE_RADIUS - 1.6;
    this.object3D.add(cap);

    const underBeacon = new THREE.Mesh(
      new THREE.SphereGeometry(0.3, 14, 10),
      createGlowMaterial(0xffe38a, 1, true)
    );
    underBeacon.position.y = -SPHERE_RADIUS - 2.0;
    this.object3D.add(underBeacon);
    this.beacons.push(underBeacon);
  }

  private buildSpire(): void {
    const trimMat = createMatteMaterial({
      color: palette.station.hullMid,
      roughness: 0.55
    });
    const hullMat = createMatteMaterial({
      color: palette.station.hull,
      roughness: 0.45
    });
    const accentMat = createMatteMaterial({
      color: 0xb4e3ff,
      roughness: 0.3,
      emissive: 0x6ac0ff,
      emissiveIntensity: 0.35
    });
    const glowMat = createGlowMaterial(0xffffff, 1, true);

    // Crown base sitting on top of the sphere.
    const crownBase = new THREE.Mesh(
      new THREE.CylinderGeometry(1.3, 2.0, 0.8, 20),
      trimMat
    );
    crownBase.position.y = SPHERE_RADIUS + 0.2;
    this.object3D.add(crownBase);

    // Sub-antennas surrounding the main spire.
    for (let i = 0; i < 6; i++) {
      const angle = (i / 6) * Math.PI * 2;
      const ant = new THREE.Mesh(
        new THREE.CylinderGeometry(0.07, 0.1, 1.6, 8),
        trimMat
      );
      ant.position.set(
        Math.cos(angle) * 1.3,
        SPHERE_RADIUS + 1.2,
        Math.sin(angle) * 1.3
      );
      ant.rotation.z = Math.cos(angle) * 0.12;
      ant.rotation.x = Math.sin(angle) * 0.12;
      this.object3D.add(ant);

      const antTip = new THREE.Mesh(
        new THREE.SphereGeometry(0.08, 8, 6),
        glowMat
      );
      antTip.position.set(
        Math.cos(angle) * 1.45,
        SPHERE_RADIUS + 2.0,
        Math.sin(angle) * 1.45
      );
      this.object3D.add(antTip);
      this.beacons.push(antTip);
    }

    // Main spire column.
    const column = new THREE.Mesh(
      new THREE.CylinderGeometry(0.55, 0.85, SPIRE_HEIGHT * 0.7, 20),
      hullMat
    );
    column.position.y = SPHERE_RADIUS + SPIRE_HEIGHT * 0.35;
    this.object3D.add(column);

    // Stacked accent rings along the spire.
    for (let i = 0; i < 4; i++) {
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(0.75 - i * 0.08, 0.08, 8, 22),
        accentMat
      );
      ring.rotation.x = Math.PI / 2;
      ring.position.y = SPHERE_RADIUS + 1.2 + i * 1.6;
      this.object3D.add(ring);
    }

    // Spire cap.
    const cap = new THREE.Mesh(
      new THREE.ConeGeometry(0.6, 1.3, 14),
      hullMat
    );
    cap.position.y = SPHERE_RADIUS + SPIRE_HEIGHT * 0.75;
    this.object3D.add(cap);

    // Apex beacon.
    const beacon = new THREE.Mesh(
      new THREE.SphereGeometry(0.35, 18, 14),
      glowMat
    );
    beacon.position.y = SPHERE_RADIUS + SPIRE_HEIGHT * 0.9;
    this.object3D.add(beacon);
    this.beacons.push(beacon);
  }

  private buildDockingArms(): void {
    const hullMat = createMatteMaterial({
      color: palette.station.hullMid,
      roughness: 0.6
    });
    const darkMat = createMatteMaterial({
      color: palette.station.hullDark,
      roughness: 0.82
    });
    const accentMat = createGlowMaterial(0xb4e3ff, 0.85, true);
    const warnMat = createGlowMaterial(0xffe38a, 1, true);

    const mkArm = (angleDeg: number): void => {
      const angle = (angleDeg * Math.PI) / 180;
      const length = 4.2;
      const radius = SPHERE_RADIUS + length / 2;

      const arm = new THREE.Mesh(
        new THREE.BoxGeometry(0.9, 0.55, length),
        hullMat
      );
      arm.position.set(Math.sin(angle) * radius, 0, Math.cos(angle) * radius);
      arm.rotation.y = angle;
      this.object3D.add(arm);

      const tipX = Math.sin(angle) * (SPHERE_RADIUS + length);
      const tipZ = Math.cos(angle) * (SPHERE_RADIUS + length);

      const pad = new THREE.Mesh(
        new THREE.CylinderGeometry(1.1, 1.35, 0.5, 18),
        darkMat
      );
      pad.position.set(tipX, 0, tipZ);
      this.object3D.add(pad);

      const padGlow = new THREE.Mesh(
        new THREE.TorusGeometry(1.1, 0.06, 8, 28),
        accentMat
      );
      padGlow.rotation.x = Math.PI / 2;
      padGlow.position.set(tipX, 0.27, tipZ);
      this.object3D.add(padGlow);

      const blinker = new THREE.Mesh(
        new THREE.SphereGeometry(0.17, 10, 8),
        warnMat
      );
      blinker.position.set(tipX, 0.55, tipZ);
      this.object3D.add(blinker);
      this.strobes.push(blinker);
    };

    // Four arms at 45° offsets from the entry (so the entry slot is clear).
    mkArm(45);
    mkArm(135);
    mkArm(225);
    mkArm(315);
  }

  private buildHalo(): THREE.Group {
    const group = new THREE.Group();
    const trimMat = createMatteMaterial({
      color: palette.station.hullMid,
      roughness: 0.55
    });
    const accentMat = createGlowMaterial(0xb4e3ff, 0.55, true);

    const torus = new THREE.Mesh(
      new THREE.TorusGeometry(HALO_RADIUS, 0.18, 14, 96),
      trimMat
    );
    torus.rotation.x = Math.PI / 2;
    group.add(torus);

    const torusAccent = new THREE.Mesh(
      new THREE.TorusGeometry(HALO_RADIUS, 0.06, 10, 96),
      accentMat
    );
    torusAccent.rotation.x = Math.PI / 2;
    torusAccent.position.y = 0.05;
    group.add(torusAccent);

    // Pulsing markers.
    const markerMat = createGlowMaterial(0xffffff, 0.92, true);
    const markerGeo = new THREE.SphereGeometry(0.25, 12, 8);
    for (let i = 0; i < 12; i++) {
      const angle = (i / 12) * Math.PI * 2 + Math.PI / 12;
      const m = new THREE.Mesh(markerGeo, markerMat);
      m.position.set(
        Math.cos(angle) * HALO_RADIUS,
        0,
        Math.sin(angle) * HALO_RADIUS
      );
      group.add(m);
    }

    // Thin support rods linking halo to the sphere.
    const rodMat = createMatteMaterial({
      color: 0x252a36,
      roughness: 0.7
    });
    const rodGeo = new THREE.BoxGeometry(0.18, 0.18, HALO_RADIUS - SPHERE_RADIUS);
    for (let i = 0; i < 3; i++) {
      const angle = (i / 3) * Math.PI * 2 + Math.PI / 6;
      const midR = (SPHERE_RADIUS + HALO_RADIUS) / 2;
      const rod = new THREE.Mesh(rodGeo, rodMat);
      rod.position.set(Math.cos(angle) * midR, 0, Math.sin(angle) * midR);
      rod.lookAt(0, 0, 0);
      group.add(rod);
    }

    return group;
  }

  private buildAura(): THREE.Mesh {
    const aura = new THREE.Mesh(
      new THREE.SphereGeometry(HALO_RADIUS + 4, 32, 20),
      createHaloMaterial(0xb4e3ff, 0.08)
    );
    return aura;
  }
}
